import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AccountsSubNav from '../components/AccountsSubNav';
import { useAcHeadEntryScreen } from './hooks/useAcHeadEntryScreen';
import { appToast } from '../../../../components/AppToast';
import {
  AccountDetailsCard,
  OrgStructureCheckboxPanel
} from '../../../../components/shared-details';

export default function AcHeadEntryScreen() {
  const { state, actions } = useAcHeadEntryScreen();

  useEffect(() => {
    if (state.error) appToast.error(state.error);
  }, [state.error]);

  useEffect(() => {
    if (state.success) appToast.success(state.success);
  }, [state.success]);

  const groupOptions = state.availableGroups.map((grpName) => ({
    value: grpName,
    label: grpName,
  }));

  // Mapping state.form to component values
  const accountDetailsValue = {
    acGroup: state.form.group,
    acName: state.form.name,
    alias: state.form.alias,
    place: state.form.place,
    costCenter: Boolean(state.form.cost_center),
    budget: state.form.budget,
    hsnSac: state.form.hsn_sac,
    agent: state.form.agent,
    remarks: state.form.remarks
  };

  const handleAccountDetailsChange = (vals) => {
    actions.patchForm('group', vals.acGroup || '');
    actions.patchForm('name', vals.acName || '');
    actions.patchForm('alias', vals.alias || '');
    actions.patchForm('place', vals.place || '');
    actions.patchForm('cost_center', vals.costCenter ? 1 : 0);
    actions.patchForm('budget', Number(vals.budget || 0));
    actions.patchForm('hsn_sac', vals.hsnSac || '');
    actions.patchForm('agent', vals.agent || '');
    actions.patchForm('remarks', vals.remarks || '');
  };

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* ── Header Title & Sub Navigation Tabs ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {state.isEdit ? 'Edit Account Head' : 'Create Account Head'}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <AccountsSubNav />
          <Link
            to="/masters/acheads/heads/list"
            className="inline-flex items-center gap-1 text-xs font-bold text-on-surface-variant hover:text-primary transition"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back</span>
          </Link>
        </div>
      </section>

      {/* ── A/c Details Card ── */}
      <div className="w-full">
        <AccountDetailsCard
          value={accountDetailsValue}
          onChange={handleAccountDetailsChange}
          groupOptions={groupOptions}
          onAddPlaceClick={() => appToast.info('Place master integration is under construction.')}
        />
      </div>

      {/* ── Full Width Bottom Organization Panels ── */}
      <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
        <div className="border-b border-outline-variant/20 pb-4 mb-6">
          <h2 className="font-display text-xl font-bold text-on-surface">Organization Scope</h2>
          <p className="text-xs text-on-surface-variant">
            Assign the Companies, Branches, and Departments where this Account Head is authorized.
          </p>
        </div>
        <OrgStructureCheckboxPanel
          selectedCompanies={state.form.companies || []}
          selectedBranches={state.form.branches || []}
          selectedDepartments={state.form.departments || []}
          onCompaniesChange={(val) => actions.patchForm('companies', val)}
          onBranchesChange={(val) => actions.patchForm('branches', val)}
          onDepartmentsChange={(val) => actions.patchForm('departments', val)}
        />
      </section>

      {/* ── Footer Actions Bar ── */}
      <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-4 shadow-sm">
        <button
          type="button"
          onClick={() => appToast.info('Audit history tracking is enabled.')}
          className="inline-flex items-center gap-1.5 rounded-xl bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant transition hover:bg-surface-container-high"
        >
          <span className="material-symbols-outlined text-sm">history</span>
          <span>History</span>
        </button>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={actions.handleCancel}
            className="rounded-xl bg-secondary-container px-6 py-2.5 text-xs font-bold text-on-secondary-container transition hover:brightness-95 cursor-pointer"
          >
            Cancel
          </button>

          {!state.isEdit && (
            <button
              type="button"
              disabled={state.isSaving}
              onClick={() => actions.handleSave({ exitAfter: false })}
              className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-6 py-2.5 text-xs font-bold text-primary transition hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
            >
              Save &amp; Continue
            </button>
          )}

          <button
            type="button"
            disabled={state.isSaving}
            onClick={() => actions.handleSave({ exitAfter: true })}
            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-container px-8 py-2.5 text-xs font-bold text-on-primary shadow-lg shadow-primary/20 transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <span className="material-symbols-outlined text-base">save</span>
            <span>{state.isSaving ? 'Saving...' : 'Save & Exit'}</span>
          </button>
        </div>
      </section>
    </div>
  );
}
